/**
 * WebsiteMicroAdapter — generic browser adapter for raw websites that have no
 * e-commerce platform SDK (the `source/website` connection).
 *
 * It reports page context from the URL and is otherwise a no-op: there is no
 * platform cart / login / checkout to read or drive, so cart reads return
 * EMPTY_CART and the CTA/event hooks do nothing.
 *
 * This is the fallback for `detectAndCreateAdapter`. Previously that defaulted
 * to the Salla adapter, which reported `platform: 'salla'` and silently read an
 * empty Salla cart on every non-Salla site (e.g. hamla.io).
 */
import type { MicroAdapter, PageContext, LiveCart } from './MicroAdapter';
import { EMPTY_CART } from './MicroAdapter';

/** Best-effort, platform-agnostic page-type detection from the URL. */
function detectPageFromUrl(): PageContext {
  const w = typeof window !== 'undefined' ? window : null;
  const url = w?.location?.href ?? '';
  const path = w?.location?.pathname ?? '';
  if (path.includes('/cart') || path.includes('/basket')) return { url, path, pageType: 'cart' };
  if (path.includes('/checkout')) return { url, path, pageType: 'checkout' };
  if (path.includes('/search')) return { url, path, pageType: 'search' };
  if (path === '/' || /^\/(ar|en)\/?$/.test(path)) return { url, path, pageType: 'home' };
  return { url, path, pageType: 'other' };
}

export function createWebsiteMicroAdapter(): MicroAdapter {
  return {
    detect() {
      return { platform: 'website', available: true };
    },

    getPageContext(): PageContext {
      return detectPageFromUrl();
    },

    async getCart(): Promise<LiveCart> {
      return EMPTY_CART;
    },

    onCartChange() {
      /* no platform cart events on a raw website */
    },

    onCheckoutIntent() {
      /* no platform checkout signal on a raw website */
    },

    onUserLogin() {
      /* no platform auth on a raw website */
    },

    async addToCart() {
      return false;
    },

    async applyCoupon() {
      return false;
    },

    destroy() {
      /* nothing wired */
    },
  };
}
